
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';

interface ApplicationFormData {
  name: string;
  email: string;
}

export default function ApplicationFormSection() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ApplicationFormData>({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d3mmgfdes84r01urdl4g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name.trim(),
          email: formData.email.trim()
        }).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: ''
        });
        // サンクスページへ遷移
        setTimeout(() => {
          navigate('/thanks');
        }, 500);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="application-form" className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* モニター会員のメリット */}
        <div className="bg-gradient-to-br from-pink-50 to-rose-100 rounded-2xl shadow-xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            <i className="ri-gift-line mr-2 text-pink-600"></i>
            モニター会員のメリット
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 左側：実績データ */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="text-xl font-bold text-gray-800 mb-4">
                <i className="ri-bar-chart-line mr-2 text-blue-600"></i>
                最近の仕事の量
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">過去3ヶ月の案件数</span>
                  <span className="font-bold text-blue-600">月平均20件</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">1件あたり募集数</span>
                  <span className="font-bold text-blue-600">平均680人分</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">商品価格帯</span>
                  <span className="font-bold text-blue-600">2,350円～13,900円</span>
                </div>
              </div>
            </div>

            {/* 右側：会員メリット */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="text-xl font-bold text-gray-800 mb-4">
                <i className="ri-user-star-line mr-2 text-pink-600"></i>
                あなたが受け取れる商品
              </h4>
              <div className="text-center">
                <div className="text-4xl font-bold text-pink-600 mb-2">毎月平均</div>
                <div className="text-2xl font-bold text-gray-800 mb-2">商品約7点</div>
                <div className="text-lg text-gray-600 mb-4">合計金額</div>
                <div className="text-3xl font-bold text-pink-600 bg-pink-50 rounded-lg py-3 px-4">
                  約23,000円相当
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl shadow-xl p-8">
          {/*応募ボタンがスクロールする対象のタイトル */}
          <div id="application-form-title" className="text-center mb-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              商品モニター会員応募
            </h2>
          </div>

          {/* 簡単さを強調するキャッチコピー */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-orange-100 text-orange-800 px-6 py-3 rounded-full font-semibold text-lg">
              <i className="ri-time-line mr-2 text-xl"></i>
              必要なのはお名前とメールアドレスだけ！30秒で完了
            </div>
          </div>
          {submitStatus === 'success' && (
            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-check-line text-green-600 text-2xl mr-4"></i>
                <div>
                  <h3 className="text-green-800 font-bold text-lg">応募完了</h3>
                  <p className="text-green-700">
                    ご応募ありがとうございます。後日ご連絡いたします。
                  </p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 text-2xl mr-4"></i>
                <div>
                  <h3 className="text-red-800 font-bold text-lg">送信エラー</h3>
                  <p className="text-red-700">
                    必須項目をすべて入力してください。
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="monitor-application">
            <div>
              <label htmlFor="name" className="block text-lg font-semibold text-gray-700 mb-2">
                お名前 *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-2">
                メールアドレス *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="example@email.com"
              />
            </div>

            <div className="text-center pt-4">
              <Button 
                type="submit" 
                size="lg" 
                disabled={isSubmitting}
                className="px-16 py-4 text-lg bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-xl font-bold shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    応募送信
                  </>
                )}
              </Button>
            </div>

          </form>
        </div>
      </div>
    </section>
  );
}
